import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { PRODUCTS, type ProductKey, useProducts } from '../context/ProductContext.js';
import { cn } from '../components/ui/cn.js';

const COLOR_MAP: Record<string, { ring: string; bg: string; check: string; border: string }> = {
  blue:   { ring: 'ring-blue-500',   bg: 'bg-blue-50',   check: 'text-blue-600',   border: 'border-blue-300'   },
  teal:   { ring: 'ring-teal-500',   bg: 'bg-teal-50',   check: 'text-teal-600',   border: 'border-teal-300'   },
  violet: { ring: 'ring-violet-500', bg: 'bg-violet-50', check: 'text-violet-600', border: 'border-violet-300' },
};

export function ProductSelectionPage() {
  const navigate              = useNavigate();
  const { setSelected }       = useProducts();
  const [picked, setPicked]   = useState<ProductKey[]>([]);

  function toggle(key: ProductKey) {
    setPicked((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }

  function handleConfirm() {
    if (picked.length === 0) return;
    setSelected(picked);
    navigate('/dashboard', { replace: true });
  }

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6">
      {/* Logo */}
      <div className="flex items-center gap-2.5 mb-12">
        <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center text-white font-bold text-base shadow-lg shadow-blue-900/50">
          H
        </div>
        <span className="font-bold text-white text-lg">Haron</span>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="w-full max-w-xl"
      >
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white">What products do you manage?</h1>
          <p className="text-slate-400 text-sm mt-2">
            Select one or more. The portal will show only the products you choose.<br />
            You can change this later in Settings.
          </p>
        </div>

        {/* Product cards */}
        <div className="space-y-3 mb-8">
          {PRODUCTS.map((p) => {
            const isSelected = picked.includes(p.key);
            const c = COLOR_MAP[p.color];
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => toggle(p.key)}
                className={cn(
                  'w-full flex items-center gap-4 px-5 py-4 rounded-xl border-2 text-left transition-all duration-150',
                  isSelected
                    ? `border-2 ${c.border} ${c.bg} ring-2 ${c.ring}/30`
                    : 'border-white/8 bg-white/4 hover:bg-white/8 hover:border-white/16',
                )}
              >
                <span className="text-3xl select-none">{p.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm font-bold', isSelected ? 'text-slate-900' : 'text-white')}>
                    {p.label}
                  </p>
                  <p className={cn('text-xs mt-0.5', isSelected ? 'text-slate-600' : 'text-slate-400')}>
                    {p.subtitle}
                  </p>
                </div>
                <div className={cn(
                  'w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all',
                  isSelected ? `${c.border} ${c.bg}` : 'border-slate-600',
                )}>
                  {isSelected && <CheckCircle2 className={cn('w-4 h-4', c.check)} />}
                </div>
              </button>
            );
          })}
        </div>

        {/* Confirm */}
        <button
          type="button"
          onClick={handleConfirm}
          disabled={picked.length === 0}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold text-sm py-3 rounded-xl transition-all shadow-lg shadow-blue-900/40"
        >
          Continue to Dashboard
          <ArrowRight className="w-4 h-4" />
        </button>

        {picked.length > 0 && (
          <p className="text-center text-xs text-slate-500 mt-3">
            {picked.length} product{picked.length > 1 ? 's' : ''} selected
          </p>
        )}
      </motion.div>
    </div>
  );
}
